/**
 * Composio Tool Utilities
 *
 * Pure functions for classifying, extracting, and formatting Composio
 * integration tools. No React — consumed by ToolUseBlock for rendering.
 *
 * Design principle: The middleware should be invisible. Users see "Gmail"
 * and "Reading your emails", not "COMPOSIO_MULTI_EXECUTE_TOOL".
 */

// ─────────────────────────────────────────────────────────────────────────────
// Tool Classification
// ─────────────────────────────────────────────────────────────────────────────

export type ComposioToolCategory =
  | "meta-search" // COMPOSIO_SEARCH_TOOLS — invisible plumbing
  | "meta-schema" // COMPOSIO_GET_TOOL_SCHEMAS — invisible plumbing
  | "meta-auth" // COMPOSIO_MANAGE_CONNECTIONS — prominent auth card
  | "meta-execute" // COMPOSIO_MULTI_EXECUTE_TOOL — branded action card
  | "meta-sandbox" // COMPOSIO_WORKSPACE_* — sandbox operations
  | "skill" // Skill tool invocations
  | "mcp-tool" // Non-Composio MCP tools (replicate, github, etc.)
  | "regular"; // Everything else (Read, Write, Bash, etc.)

const COMPOSIO_TOOL_MAP: Record<string, ComposioToolCategory> = {
  COMPOSIO_SEARCH_TOOLS: "meta-search",
  COMPOSIO_GET_TOOL_SCHEMAS: "meta-schema",
  COMPOSIO_MANAGE_CONNECTIONS: "meta-auth",
  COMPOSIO_MULTI_EXECUTE_TOOL: "meta-execute",
  COMPOSIO_CHECK_ACTIVE_CONNECTION: "meta-search",
  COMPOSIO_WORKSPACE_CREATE: "meta-sandbox",
  COMPOSIO_WORKSPACE_EXECUTE: "meta-sandbox",
};

/**
 * Classify a tool into a rendering category.
 * Handles both raw names and MCP-prefixed names (mcp__composio__TOOL).
 */
export function classifyTool(
  toolName: string | undefined
): ComposioToolCategory {
  if (!toolName) return "regular";
  if (toolName === "Skill") return "skill";

  // Strip MCP prefix: "mcp__composio__COMPOSIO_SEARCH_TOOLS" → "COMPOSIO_SEARCH_TOOLS"
  const baseName = toolName.replace(/^mcp__[a-zA-Z0-9_]+__/, "");

  if (COMPOSIO_TOOL_MAP[baseName]) return COMPOSIO_TOOL_MAP[baseName];

  // Catch-all: any COMPOSIO_* tool not in the map → sandbox operation
  // Prevents raw MCP names from reaching the regular tool renderer
  if (baseName.startsWith("COMPOSIO_")) return "meta-sandbox";

  // Non-Composio MCP tools (mcp__replicate__search_models, mcp__github__list_repos, etc.)
  // These get a proper branded card, not a compact pill
  if (toolName.startsWith("mcp__")) return "mcp-tool";

  return "regular";
}

// ─────────────────────────────────────────────────────────────────────────────
// Brand-icon slug + humanized name (permission card)
// ─────────────────────────────────────────────────────────────────────────────

const COMPOSIO_TOOL_PREFIX = "mcp__composio__";

/** Meta-tools that EXECUTE or PROXY another tool — their own name says nothing
 *  about the side effect. Mirrors COMPOSIO_EXECUTOR_META_TOOLS in the agent-sdk
 *  policy (packages/agent-sdk/src/policy/composio-naming.ts). */
const COMPOSIO_EXECUTOR_META_TOOLS = new Set([
  "mcp__composio__COMPOSIO_MULTI_EXECUTE_TOOL",
  "mcp__composio__COMPOSIO_REMOTE_WORKBENCH",
  "mcp__composio__COMPOSIO_WORKSPACE_EXECUTE",
  "mcp__composio__COMPOSIO_WORKSPACE_CREATE",
]);

/** Discovery/connection meta-tool namespaces (COMPOSIO_*, MY_*) that name no
 *  single app. Real per-service tools start with the toolkit (GMAIL_, SLACK_). */
const COMPOSIO_META_TOOL_PATTERNS: readonly RegExp[] = [
  /^mcp__composio__COMPOSIO_/i,
  /^mcp__composio__MY_/i,
];

/** Slug fields Composio uses inside an executor's `tools[]` payload. */
const INNER_SLUG_FIELDS = [
  "tool_slug",
  "tool_name",
  "toolName",
  "name",
  "action",
  "action_name",
  "slug",
] as const;

function innerSlugOf(entry: unknown): string | null {
  if (typeof entry === "string") return entry.trim() || null;
  if (entry && typeof entry === "object") {
    for (const field of INNER_SLUG_FIELDS) {
      const v = (entry as Record<string, unknown>)[field];
      if (typeof v === "string" && v.trim()) return v.trim();
    }
  }
  return null;
}

/**
 * The toolkit slug for a permission card's brand icon ("gmail", "github", …),
 * or null when no single app names the call. Replicated locally from the
 * agent-sdk policy helper `appSlugForToolCall` (apps/web never imports
 * @vestra/agent-sdk) so the card icon is derived from the SAME unwrap the gate
 * uses and can never disagree with the decision:
 *   - direct Composio tool (`mcp__composio__GMAIL_SEND_EMAIL`) → "gmail"
 *   - executor meta-tool whose inner actions ALL share one toolkit → that slug
 *   - executor spanning several apps, an opaque payload (REMOTE_WORKBENCH), a
 *     discovery meta-tool, or a non-Composio tool → null (generic glyph).
 * TOTAL — never throws.
 */
export function appSlugForToolCall(call: {
  toolName?: string;
  toolInput?: unknown;
}): string | null {
  const toolName = call?.toolName;
  if (typeof toolName !== "string" || toolName.length === 0) return null;

  if (COMPOSIO_EXECUTOR_META_TOOLS.has(toolName)) {
    const input = call.toolInput;
    if (!input || typeof input !== "object") return null;
    const tools = (input as Record<string, unknown>).tools;
    if (!Array.isArray(tools) || tools.length === 0) return null;
    const toolkits: string[] = [];
    for (const entry of tools) {
      const raw = innerSlugOf(entry);
      if (raw === null) return null; // one unreadable entry ⇒ could run anything
      const slug = raw.toUpperCase();
      const underscore = slug.indexOf("_");
      toolkits.push(
        (underscore > 0 ? slug.slice(0, underscore) : slug).toLowerCase()
      );
    }
    const first = toolkits[0];
    return toolkits.every((t) => t === first) ? first : null;
  }

  // Discovery/connection meta-tools (SEARCH/MANAGE/CHECK, MY_*) name no app.
  if (COMPOSIO_META_TOOL_PATTERNS.some((p) => p.test(toolName))) return null;

  if (toolName.startsWith(COMPOSIO_TOOL_PREFIX)) {
    const suffix = toolName.slice(COMPOSIO_TOOL_PREFIX.length);
    const idx = suffix.indexOf("_");
    return (idx > 0 ? suffix.slice(0, idx) : suffix).toLowerCase();
  }
  return null;
}

/**
 * Last-resort card title: a title-cased, de-prefixed tool name. Strips the MCP
 * namespace and the COMPOSIO_ prefix so the raw `mcp__composio__…` form is never
 * shown ("mcp__composio__COMPOSIO_REMOTE_WORKBENCH" → "Remote Workbench",
 * "mcp__github__create_pull_request" → "Create Pull Request", "Bash" → "Bash").
 */
export function humanizeToolName(toolName: string): string {
  const base = toolName
    .replace(/^mcp__[a-zA-Z0-9_]+?__/, "")
    .replace(/^COMPOSIO_/, "");
  const humanized = base
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
  return humanized || toolName;
}

// ─────────────────────────────────────────────────────────────────────────────
// Real Tool Extraction (from COMPOSIO_MULTI_EXECUTE_TOOL input)
// ─────────────────────────────────────────────────────────────────────────────

export interface RealToolInfo {
  /** Raw tool name, e.g. "GMAIL_FETCH_EMAILS" */
  toolName: string;
  /** Integration slug, e.g. "gmail" */
  integration: string;
  /** Human-readable action, e.g. "Fetch Emails" */
  humanAction: string;
  /** Human label combining integration + action, e.g. "Reading your emails" */
  humanLabel: string;
  /** Tool parameters (cleaned — no session_id, thought, memory) */
  params: Record<string, unknown>;
}

/** Internal parameters to strip from display */
const INTERNAL_PARAMS = new Set([
  "session_id",
  "sessionId",
  "memory",
  "thought",
  "current_step",
  "tool_name",
  "toolName",
  "tool_slug",
  "toolSlug",
  "sync_response_to_workbench",
]);

/**
 * Extract the real tool from COMPOSIO_MULTI_EXECUTE_TOOL's input payload.
 *
 * Input shape:
 * ```json
 * {
 *   "tools": [{ "tool_name": "GMAIL_FETCH_EMAILS", "parameters": {...} }],
 *   "thought": "I'll fetch the user's recent emails",
 *   "session_id": "..."
 * }
 * ```
 */
export function extractRealTool(toolInput: unknown): RealToolInfo | null {
  if (!toolInput || typeof toolInput !== "object") return null;

  const input = toolInput as Record<string, unknown>;
  let rawName: string | undefined;
  let rawParams: Record<string, unknown> = {};

  // 1. Check tools array (object or string elements)
  const tools = input.tools as Array<unknown> | undefined;
  if (Array.isArray(tools) && tools[0]) {
    if (typeof tools[0] === "string") {
      rawName = tools[0] as string;
      rawParams = (input.arguments ||
        input.parameters ||
        input.params ||
        input.input ||
        {}) as Record<string, unknown>;
    } else if (typeof tools[0] === "object" && tools[0] !== null) {
      const ft = tools[0] as Record<string, unknown>;
      rawName = (ft.tool_slug ||
        ft.tool_name ||
        ft.toolName ||
        ft.name ||
        ft.action ||
        ft.action_name ||
        ft.slug) as string;
      rawParams = (ft.arguments ||
        ft.parameters ||
        ft.params ||
        ft.input ||
        {}) as Record<string, unknown>;
    }
  }

  // 2. Check actions array (object or string elements)
  if (!rawName) {
    const actions = input.actions as Array<unknown> | undefined;
    if (Array.isArray(actions) && actions[0]) {
      if (typeof actions[0] === "string") {
        rawName = actions[0] as string;
        rawParams = (input.arguments ||
          input.parameters ||
          input.params ||
          input.input ||
          {}) as Record<string, unknown>;
      } else if (typeof actions[0] === "object" && actions[0] !== null) {
        const fa = actions[0] as Record<string, unknown>;
        rawName = (fa.tool_slug ||
          fa.action ||
          fa.tool_name ||
          fa.toolName ||
          fa.action_name ||
          fa.name ||
          fa.slug) as string;
        rawParams = (fa.arguments ||
          fa.params ||
          fa.parameters ||
          fa.input ||
          {}) as Record<string, unknown>;
      }
    }
  }

  // 3. Check direct top-level fields
  if (!rawName) {
    rawName = (input.tool_slug ||
      input.tool_name ||
      input.toolName ||
      input.action ||
      input.action_name ||
      input.slug) as string;
    if (rawName)
      rawParams = (input.arguments ||
        input.parameters ||
        input.params ||
        input.input ||
        {}) as Record<string, unknown>;
  }

  // 4. Check nested request wrapper
  if (!rawName && input.request && typeof input.request === "object") {
    const req = input.request as Record<string, unknown>;
    rawName = (req.tool_slug ||
      req.tool_name ||
      req.toolName ||
      req.action ||
      req.action_name ||
      req.slug) as string;
    if (rawName)
      rawParams = (req.arguments ||
        req.parameters ||
        req.params ||
        req.input ||
        {}) as Record<string, unknown>;
  }

  // 5. Fallback: scan string values for Composio action pattern (UPPERCASE_ACTION_NAME)
  if (!rawName) {
    for (const value of Object.values(input)) {
      if (
        typeof value === "string" &&
        /^[A-Z][A-Z0-9]*(_[A-Z0-9]+){2,}$/.test(value)
      ) {
        rawName = value;
        rawParams = (input.arguments ||
          input.parameters ||
          input.params ||
          input.input ||
          {}) as Record<string, unknown>;
        break;
      }
    }
  }

  if (!rawName) return null;

  const integration = extractIntegration(rawName);
  const humanAction = humanizeToolAction(rawName, integration);

  // Use agent's "thought" as display text if available, otherwise generate one
  const thought = (input.thought || input.current_step) as string | undefined;
  const humanLabel = thought || generateHumanLabel(integration, humanAction);

  // Clean parameters — remove internal fields
  const params: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(rawParams)) {
    if (!INTERNAL_PARAMS.has(key)) {
      params[key] = value;
    }
  }

  return { toolName: rawName, integration, humanAction, humanLabel, params };
}

/** Fields, in priority order, where the agent narrates its intent. */
const STEP_FIELDS = [
  "thought",
  "current_step",
  "reasoning",
  "instruction",
  "description",
  "goal",
  "step",
  "task",
];

/** Whole-word, case-insensitive membership — avoids "flaws" matching "aws". */
function mentions(text: string, term: string): boolean {
  if (term.length < 2) return false; // skip 1-char names like X (Twitter)
  return new RegExp(
    `\\b${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`,
    "i"
  ).test(text);
}

/**
 * Extract a human-readable step label (+ best-effort integration) from a Composio
 * tool's INPUT, for tools that arrive without a server-side display.
 *
 * Why: the native arm only server-enriches the Skill tool, so other Composio
 * tools (notably COMPOSIO_REMOTE_WORKBENCH) reach the client bare and render as
 * "Remote Workbench". But the agent narrates its intent in the input (the SAME
 * `thought`/`current_step` field the MULTI_EXECUTE path surfaces), so we can show
 * "Fetch Nandini's email…" instead. Returns null when no narration is present.
 */
export function extractComposioStepLabel(
  toolInput: unknown
): { label: string; integration: string | null } | null {
  if (!toolInput || typeof toolInput !== "object") return null;
  const input = toolInput as Record<string, unknown>;

  let narration: string | null = null;
  for (const key of STEP_FIELDS) {
    const v = input[key];
    if (typeof v === "string" && v.trim()) {
      narration = v.trim();
      break;
    }
  }
  if (!narration) return null;

  // A slug ("download_closing_attachment") → humanize; a sentence → verbatim.
  const isSlug = !narration.includes(" ") && /[_-]/.test(narration);
  const label = isSlug
    ? narration.replace(/[_-]/g, " ").replace(/^\w/, (c) => c.toUpperCase())
    : narration;

  // Best-effort integration inference (for the icon) — slug first, then name.
  let integration: string | null = null;
  for (const slug of Object.keys(INTEGRATION_DISPLAY)) {
    if (mentions(narration, slug)) {
      integration = slug;
      break;
    }
  }
  if (!integration) {
    for (const [slug, info] of Object.entries(INTEGRATION_DISPLAY)) {
      if (mentions(narration, info.name)) {
        integration = slug;
        break;
      }
    }
  }

  return { label: truncate(label, 120), integration };
}

/**
 * Extract integration slug from a Composio tool name.
 * "GMAIL_FETCH_EMAILS" → "gmail"
 * "GITHUB_LIST_REPOS" → "github"
 * "GOOGLE_CALENDAR_CREATE_EVENT" → "googlecalendar"
 */
function extractIntegration(toolName: string): string {
  const name = toolName.toUpperCase();

  // Multi-word integrations (check these first)
  const multiWord: Record<string, string> = {
    GOOGLE_CALENDAR_: "googlecalendar",
    GOOGLE_DRIVE_: "googledrive",
    GOOGLE_SHEETS_: "googlesheets",
    GOOGLE_DOCS_: "googledocs",
  };
  for (const [prefix, slug] of Object.entries(multiWord)) {
    if (name.startsWith(prefix)) return slug;
  }

  // Single-word: first segment before underscore
  const firstUnderscore = name.indexOf("_");
  if (firstUnderscore > 0) {
    return name.substring(0, firstUnderscore).toLowerCase();
  }

  return name.toLowerCase();
}

/**
 * Convert "GMAIL_FETCH_EMAILS" → "Fetch Emails"
 * Strips integration prefix and humanizes the rest.
 */
function humanizeToolAction(toolName: string, integration: string): string {
  let action = toolName.toUpperCase();

  // Strip known multi-word prefixes
  const multiPrefixes = [
    "GOOGLE_CALENDAR_",
    "GOOGLE_DRIVE_",
    "GOOGLE_SHEETS_",
    "GOOGLE_DOCS_",
  ];
  for (const prefix of multiPrefixes) {
    if (action.startsWith(prefix)) {
      action = action.slice(prefix.length);
      break;
    }
  }

  // Strip single-word prefix
  if (action.startsWith(integration.toUpperCase() + "_")) {
    action = action.slice(integration.length + 1);
  }

  // "FETCH_EMAILS" → "Fetch Emails"
  return action
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Generate a natural-language label for what the tool is doing.
 * Maps common action verbs to present continuous form.
 */
function generateHumanLabel(integration: string, action: string): string {
  const displayName = getIntegrationDisplayName(integration);
  const firstWord = action.split(" ")[0].toLowerCase();
  const rest = action.split(" ").slice(1).join(" ").toLowerCase();

  // Map action verbs to present continuous
  const verbMap: Record<string, string> = {
    fetch: "Fetching",
    list: "Listing",
    get: "Getting",
    create: "Creating",
    send: "Sending",
    update: "Updating",
    delete: "Deleting",
    search: "Searching",
    read: "Reading",
    reply: "Replying to",
    star: "Starring",
    archive: "Archiving",
    move: "Moving",
    add: "Adding",
    remove: "Removing",
    find: "Finding",
    check: "Checking",
    modify: "Modifying",
    set: "Setting",
    mark: "Marking",
    label: "Labeling",
  };

  const verb =
    verbMap[firstWord] ||
    firstWord.charAt(0).toUpperCase() + firstWord.slice(1) + "ing";

  return `${verb} ${rest || "data"} on ${displayName}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// MCP Tool Name Parsing
// ─────────────────────────────────────────────────────────────────────────────

export interface McpToolInfo {
  /** Provider slug, e.g. "replicate" */
  provider: string;
  /** Provider display name, e.g. "Replicate" */
  providerName: string;
  /** Human-readable action, e.g. "Search Models" */
  action: string;
  /** Present-continuous label, e.g. "Searching models on Replicate" */
  humanLabel: string;
}

/**
 * Parse an MCP tool name into human-readable parts.
 * "mcp__replicate__search_models" → { provider: "replicate", action: "Search Models", ... }
 */
export function parseMcpToolName(toolName: string): McpToolInfo {
  const match = toolName.match(/^mcp__([a-zA-Z0-9_-]+)__(.+)$/);
  if (!match) {
    return {
      provider: "tool",
      providerName: "Tool",
      action: toolName,
      humanLabel: toolName,
    };
  }

  const provider = match[1].toLowerCase();
  // Handle nested provider names like "plugin_playwright_playwright"
  const providerClean = provider.replace(/^plugin_/, "").split("_")[0];
  const providerName = getIntegrationDisplayName(providerClean);

  const rawAction = match[2];
  const action = rawAction
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");

  // Custom labels for specific providers
  const customLabel = CUSTOM_MCP_LABELS[providerClean]?.[rawAction];
  const humanLabel = customLabel || generateHumanLabel(providerClean, action);

  return { provider: providerClean, providerName, action, humanLabel };
}

/** Custom human-readable labels for MCP tools that need natural phrasing */
const CUSTOM_MCP_LABELS: Record<string, Record<string, string>> = {
  memory: {
    search: "Searching your memories",
    get: "Recalling a memory",
    store: "Remembering this",
    forget: "Vestratting a memory",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Integration Display Info
// ─────────────────────────────────────────────────────────────────────────────

const INTEGRATION_DISPLAY: Record<string, { name: string; color: string }> = {
  gmail: { name: "Gmail", color: "#EA4335" },
  github: { name: "GitHub", color: "#24292e" },
  slack: { name: "Slack", color: "#4A154B" },
  notion: { name: "Notion", color: "#000000" },
  googledrive: { name: "Google Drive", color: "#4285F4" },
  googlecalendar: { name: "Google Calendar", color: "#4285F4" },
  googlesheets: { name: "Google Sheets", color: "#0F9D58" },
  googledocs: { name: "Google Docs", color: "#4285F4" },
  trello: { name: "Trello", color: "#0079BF" },
  jira: { name: "Jira", color: "#0052CC" },
  linear: { name: "Linear", color: "#5E6AD2" },
  discord: { name: "Discord", color: "#5865F2" },
  twitter: { name: "X", color: "#71767b" },
  hubspot: { name: "HubSpot", color: "#FF7A59" },
  salesforce: { name: "Salesforce", color: "#00A1E0" },
  stripe: { name: "Stripe", color: "#635BFF" },
  airtable: { name: "Airtable", color: "#18BFFF" },
  asana: { name: "Asana", color: "#F06A6A" },
  figma: { name: "Figma", color: "#F24E1E" },
  zoom: { name: "Zoom", color: "#2D8CFF" },
  dropbox: { name: "Dropbox", color: "#0061FF" },
  spotify: { name: "Spotify", color: "#1DB954" },
  youtube: { name: "YouTube", color: "#FF0000" },
  twilio: { name: "Twilio", color: "#F22F46" },
  mailchimp: { name: "Mailchimp", color: "#FFE01B" },
  intercom: { name: "Intercom", color: "#1F8DED" },
  reddit: { name: "Reddit", color: "#FF4500" },
  instagram: { name: "Instagram", color: "#E4405F" },
  linkedin: { name: "LinkedIn", color: "#0A66C2" },
  pinterest: { name: "Pinterest", color: "#E60023" },
  shopify: { name: "Shopify", color: "#96BF48" },
  zendesk: { name: "Zendesk", color: "#03363D" },
  clickup: { name: "ClickUp", color: "#7B68EE" },
  monday: { name: "Monday", color: "#FF3D57" },
  todoist: { name: "Todoist", color: "#E44332" },
  telegram: { name: "Telegram", color: "#26A5E4" },
  replicate: { name: "Replicate", color: "#000000" },
  supabase: { name: "Supabase", color: "#3ECF8E" },
  vercel: { name: "Vercel", color: "#000000" },
  openai: { name: "OpenAI", color: "#10A37F" },
  anthropic: { name: "Anthropic", color: "#D97757" },
  firebase: { name: "Firebase", color: "#FFCA28" },
  aws: { name: "AWS", color: "#FF9900" },
  cloudflare: { name: "Cloudflare", color: "#F48120" },
  postgresql: { name: "PostgreSQL", color: "#336791" },
  mongodb: { name: "MongoDB", color: "#47A248" },
  redis: { name: "Redis", color: "#DC382D" },
  docker: { name: "Docker", color: "#2496ED" },
  npm: { name: "npm", color: "#CB3837" },
  playwright: { name: "Playwright", color: "#2EAD33" },
  puppeteer: { name: "Puppeteer", color: "#40B5A4" },
  granola: { name: "Granola", color: "#B2C248" },
  memory: { name: "Memory", color: "#8B5CF6" },
  sentry: { name: "Sentry", color: "#362D59" },
  datadog: { name: "Datadog", color: "#632CA6" },
  twitch: { name: "Twitch", color: "#9146FF" },
  higgsfield: { name: "Higgsfield", color: "#D1FE17" },
  // Ours (the Studio): a palette hex, --color-pink, never a vendor colour.
  fal: { name: "Studio", color: "#EC4899" },

  "create-skill": { name: "Create Skill", color: "#F97316" },
  "create-agent": { name: "Create Agent", color: "#ef4444" },
  "create-routine": { name: "Create Routine", color: "#22C55E" },
};

/**
 * Composio's MCP-class toolkits carry an `_mcp` slug suffix (granola_mcp, …) that is
 * transport plumbing, not brand. Strip it for every DISPLAY lookup (name, color, logo)
 * while the functional slug (connect/verify routes, catalog queries) stays verbatim.
 */
export function displayIntegrationSlug(slug: string): string {
  return slug.toLowerCase().replace(/_mcp$/, "");
}

/** Server/catalog display names carry the same suffix as a trailing " MCP"
 *  word; a runner-minted name carries it as the raw `_mcp` ("Granola_mcp"). */
export function cleanIntegrationDisplayName(
  name: string | undefined
): string | undefined {
  const cleaned = name?.replace(/(\s+|_)mcp$/i, "").trim();
  return cleaned || undefined;
}

/**
 * The name a connect ask wears. Our catalog knows the brand for every slug it
 * carries ("googlecalendar" → "Google Calendar") and wins; the name the runner
 * or server provided stands in for a toolkit we do not know (the runner mints
 * it by capitalizing the slug — "Googlecalendar", "Granola_mcp" — which is why
 * it never outranks the catalog: staging 09-03); the slug itself is the floor.
 */
export function resolveIntegrationDisplayName(
  slug: string,
  provided?: string
): string {
  const displaySlug = displayIntegrationSlug(slug);
  return (
    INTEGRATION_DISPLAY[displaySlug]?.name ||
    cleanIntegrationDisplayName(provided) ||
    displaySlug.charAt(0).toUpperCase() + displaySlug.slice(1)
  );
}

export function getIntegrationDisplayName(slug: string): string {
  const displaySlug = displayIntegrationSlug(slug);
  return (
    INTEGRATION_DISPLAY[displaySlug]?.name ||
    displaySlug.charAt(0).toUpperCase() + displaySlug.slice(1)
  );
}

export function getIntegrationColor(slug: string): string {
  slug = displayIntegrationSlug(slug);
  if (INTEGRATION_DISPLAY[slug]?.color) return INTEGRATION_DISPLAY[slug].color;

  // Deterministic color based on string hash
  let hash = 0;
  for (let i = 0; i < slug.length; i++) {
    hash = slug.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 55%, 50%)`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Display Text for Meta Tools
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Get human-readable display text for meta/plumbing tools.
 */
export function getMetaToolDisplayText(
  category: ComposioToolCategory,
  toolInput: unknown,
  status: string
): string {
  const input = (toolInput || {}) as Record<string, unknown>;

  switch (category) {
    case "meta-search": {
      const query = input.query as string | undefined;
      if (query) return `Finding tools for "${truncate(query, 30)}"`;
      return status === "completed"
        ? "Tools configured"
        : "Setting up tools...";
    }
    case "meta-schema":
      return status === "completed" ? "Tools ready" : "Loading tool details...";
    case "meta-auth": {
      let toolkit =
        ((input.app || input.toolkit || input.appName) as string) || "";
      // Handle plural array fields (COMPOSIO_MANAGE_CONNECTIONS uses toolkits: ["slack"])
      if (!toolkit) {
        const toolkits = input.toolkits || input.apps || input.app_names;
        if (
          Array.isArray(toolkits) &&
          toolkits.length > 0 &&
          typeof toolkits[0] === "string"
        ) {
          toolkit = toolkits[0] as string;
        }
      }
      const displayName = toolkit
        ? getIntegrationDisplayName(toolkit.toLowerCase())
        : "service";
      return `Connect ${displayName}`;
    }
    case "meta-sandbox":
      return status === "completed"
        ? "Workspace ready"
        : "Preparing workspace...";
    default:
      return "Processing...";
  }
}

/**
 * System skill slugs that get special card rendering (layers icon, never show "Failed").
 * The Skill tool often reports error status for these even when the operation succeeded.
 */
export const SYSTEM_SKILL_SLUGS = new Set([
  "create-skill",
  "create-agent",
  "create-routine",
]);

/**
 * Extract skill name from Skill tool input and format it.
 * "inbox-triage" → "Inbox Triage"
 * "essentials:report-generation" → "Report Generation" (ability prefix stripped;
 * the ability is surfaced separately via display.abilityName). Mirrors the
 * sandbox-side getSkillDisplayName so this client fallback stays consistent when
 * a block arrives without an enriched display (old persisted blocks, edge cases).
 */
export function getSkillDisplayName(toolInput: unknown): string {
  if (!toolInput || typeof toolInput !== "object") return "Running Skill";
  const input = toolInput as Record<string, unknown>;
  const raw = (input.skill || input.name || "") as string;
  const colon = raw.indexOf(":");
  const bare = colon >= 0 ? raw.slice(colon + 1) : raw;
  if (!bare) return "Running Skill";

  return bare.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─────────────────────────────────────────────────────────────────────────────
// Result Formatting
// ─────────────────────────────────────────────────────────────────────────────

export interface FormattedResultItem {
  primary: string;
  secondary?: string;
  meta?: string;
}

export type FormattedResult =
  | { type: "list"; title: string; items: FormattedResultItem[] }
  | {
      type: "single";
      title: string;
      fields: Array<{ label: string; value: string }>;
    }
  | { type: "success"; message: string }
  | { type: "error"; message: string }
  | {
      type: "keyvalue";
      title: string;
      entries: Array<{ key: string; value: string }>;
    };

/**
 * Format a Composio result into a structured display.
 *
 * Composio response shape:
 * ```json
 * { "successful": true, "data": { "response_data": { ... } }, "error": null }
 * ```
 *
 * Falls back gracefully for unexpected shapes.
 */
export function formatComposioResult(
  result: unknown,
  realTool: RealToolInfo | null
): FormattedResult | null {
  if (!result) return null;

  // Parse string results
  let parsed: unknown = result;
  if (typeof result === "string") {
    try {
      parsed = JSON.parse(result);
    } catch {
      return null;
    }
  }

  // Unwrap MCP response envelope: [{"type":"text","text":"{...}"}]
  parsed = unwrapMcpEnvelope(parsed);

  if (typeof parsed !== "object" || parsed === null) return null;

  const obj = parsed as Record<string, unknown>;

  // Check for Composio error
  if (obj.successful === false || obj.error) {
    const errMsg = (
      typeof obj.error === "string"
        ? obj.error
        : (obj.error as Record<string, unknown>)?.message
    ) as string;
    return { type: "error", message: errMsg || "Action failed" };
  }

  // Unwrap Composio response: data.response_data
  let responseData = unwrapComposioResponse(obj);
  if (!responseData) return null;

  // If responseData is a string, try to parse it
  if (typeof responseData === "string") {
    try {
      responseData = JSON.parse(responseData);
    } catch {
      return null;
    }
  }

  // Determine action type from tool name for contextual formatting
  const actionVerb = realTool
    ? realTool.toolName.split("_").slice(1)[0]?.toLowerCase()
    : "";
  const isMutationAction = [
    "send",
    "create",
    "update",
    "delete",
    "star",
    "archive",
    "move",
    "add",
    "remove",
    "reply",
    "label",
    "mark",
  ].includes(actionVerb);

  // Array of items → list view
  if (Array.isArray(responseData)) {
    const items = responseData
      .slice(0, 10)
      .map((item: unknown) => formatListItem(item));
    const integration = realTool
      ? getIntegrationDisplayName(realTool.integration)
      : "";
    const count = responseData.length;
    const title = `${count} ${count === 1 ? "result" : "results"}${
      integration ? ` from ${integration}` : ""
    }`;
    return { type: "list", title, items };
  }

  // Single object
  if (typeof responseData === "object" && responseData !== null) {
    const data = responseData as Record<string, unknown>;

    // Mutation success — simple confirmation
    if (isMutationAction) {
      const id = data.id || data.threadId || data.messageId || "";
      const name = data.name || data.title || data.subject || "";
      const msg = name
        ? `${realTool?.humanAction || "Action"} completed: ${name}`
        : `${realTool?.humanAction || "Action"} completed successfully`;
      return {
        type: "success",
        message: id ? `${msg} (${truncate(String(id), 20)})` : msg,
      };
    }

    // Composio internal fields that are not useful to end users
    const COMPOSIO_INTERNAL_KEYS = new Set([
      "session",
      "totalCount",
      "total_count",
      "successCount",
      "success_count",
      "errorCount",
      "error_count",
      "response_data",
      "instructions",
      "id",
      "successful",
      "error",
      "execution_details",
      "metadata",
    ]);

    // Object with recognizable fields → detail card
    const title = (data.name ||
      data.title ||
      data.subject ||
      data.full_name ||
      "") as string;
    if (title) {
      const fields: Array<{ label: string; value: string }> = [];
      const skipKeys = new Set([
        "name",
        "title",
        "subject",
        "full_name",
        ...COMPOSIO_INTERNAL_KEYS,
      ]);
      for (const [key, value] of Object.entries(data)) {
        if (skipKeys.has(key) || value === null || value === undefined)
          continue;
        if (typeof value === "object") continue; // skip nested objects
        fields.push({
          label: humanizeKey(key),
          value: truncate(String(value), 100),
        });
        if (fields.length >= 6) break;
      }
      // If no meaningful fields after filtering, return success instead of empty card
      if (fields.length === 0) {
        return {
          type: "success",
          message: realTool?.humanAction || "Completed successfully",
        };
      }
      return { type: "single", title, fields };
    }

    // Fallback: key-value display
    const entries: Array<{ key: string; value: string }> = [];
    for (const [key, value] of Object.entries(data)) {
      if (value === null || value === undefined) continue;
      if (COMPOSIO_INTERNAL_KEYS.has(key)) continue;
      const strValue =
        typeof value === "object" ? JSON.stringify(value) : String(value);
      entries.push({ key: humanizeKey(key), value: truncate(strValue, 120) });
      if (entries.length >= 8) break;
    }
    if (entries.length > 0) {
      return {
        type: "keyvalue",
        title: realTool?.humanAction || "Result",
        entries,
      };
    }
  }

  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Unwrap MCP response envelope.
 * MCP wraps tool results as: [{"type":"text","text":"{\\"successful\\":true,...}"}]
 * This extracts and parses the inner JSON.
 */
function unwrapMcpEnvelope(parsed: unknown): unknown {
  if (!Array.isArray(parsed)) return parsed;
  if (parsed.length === 0) return parsed;

  const first = parsed[0];
  if (
    typeof first === "object" &&
    first !== null &&
    (first as Record<string, unknown>).type === "text" &&
    typeof (first as Record<string, unknown>).text === "string"
  ) {
    try {
      return JSON.parse((first as Record<string, unknown>).text as string);
    } catch {
      return parsed;
    }
  }

  return parsed;
}

function unwrapComposioResponse(obj: Record<string, unknown>): unknown {
  // Standard Composio: { data: { response_data: ... } }
  if (obj.data && typeof obj.data === "object") {
    const data = obj.data as Record<string, unknown>;
    if (data.response_data !== undefined) return data.response_data;
    return data;
  }
  // Already unwrapped — look for meaningful top-level keys
  if (obj.response_data !== undefined) return obj.response_data;
  // Composio wraps text results
  if (obj.result !== undefined) return obj.result;
  // If it has successful:true but no data wrapper, the object itself has the data
  if (obj.successful === true) {
    const copy = { ...obj };
    delete copy.successful;
    delete copy.error;
    return Object.keys(copy).length > 0 ? copy : null;
  }
  return null;
}

function formatListItem(item: unknown): FormattedResultItem {
  if (typeof item === "string") return { primary: truncate(item, 80) };
  if (typeof item !== "object" || item === null)
    return { primary: String(item) };

  const obj = item as Record<string, unknown>;

  // Try common primary fields
  const primary = (obj.subject ||
    obj.title ||
    obj.name ||
    obj.full_name ||
    obj.label ||
    obj.snippet ||
    obj.description ||
    obj.text ||
    obj.content ||
    "") as string;

  // Try common secondary fields
  const secondary = (obj.from ||
    obj.sender ||
    obj.author ||
    obj.owner ||
    obj.email ||
    obj.login ||
    obj.username ||
    "") as string;

  // Try common meta fields
  const meta = formatMeta(obj);

  return {
    primary: truncate(primary || summarizeObject(obj), 80),
    secondary: secondary ? truncate(formatSecondary(secondary), 50) : undefined,
    meta: meta || undefined,
  };
}

function formatSecondary(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "object" && value !== null) {
    const obj = value as Record<string, unknown>;
    return (obj.email ||
      obj.name ||
      obj.address ||
      JSON.stringify(value)) as string;
  }
  return String(value);
}

function formatMeta(obj: Record<string, unknown>): string | null {
  // Date/time
  const dateField =
    obj.date ||
    obj.created_at ||
    obj.createdAt ||
    obj.updated_at ||
    obj.updatedAt ||
    obj.internalDate;
  if (dateField) {
    return formatRelativeTime(dateField);
  }
  // Count-like fields
  const count = obj.stargazers_count || obj.stars || obj.forks || obj.watchers;
  if (count !== undefined) return `★ ${count}`;
  return null;
}

function formatRelativeTime(value: unknown): string {
  try {
    const date =
      typeof value === "number"
        ? new Date(value > 1e12 ? value : value * 1000) // handle ms vs seconds
        : new Date(String(value));

    if (isNaN(date.getTime())) return "";

    const now = Date.now();
    const diff = now - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 30) return `${days}d ago`;
    return date.toLocaleDateString();
  } catch {
    return "";
  }
}

function summarizeObject(obj: Record<string, unknown>): string {
  const keys = Object.keys(obj).filter(
    (k) => obj[k] !== null && obj[k] !== undefined
  );
  if (keys.length === 0) return "(empty)";
  // Take first string value
  for (const key of keys) {
    if (typeof obj[key] === "string" && (obj[key] as string).length > 0) {
      return truncate(obj[key] as string, 60);
    }
  }
  return `${keys.length} fields`;
}

function humanizeKey(key: string): string {
  return key
    .replace(/[_-]/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/^Id$/, "ID")
    .replace(/\bUrl\b/, "URL");
}

function truncate(str: string, max: number): string {
  if (str.length <= max) return str;
  return str.slice(0, max - 1) + "…";
}

/**
 * Extract integration slug from a meta-search tool's query input.
 * "Find tools for gmail email" → "gmail"
 * "Google Drive files" → "googledrive" (display names too; whole words only)
 */
export function extractIntegrationFromQuery(toolInput: unknown): string | null {
  if (!toolInput || typeof toolInput !== "object") return null;
  const input = toolInput as Record<string, unknown>;
  const query = ((input.query || input.app || "") as string).toLowerCase();
  if (!query) return null;

  for (const slug of Object.keys(INTEGRATION_DISPLAY)) {
    if (mentions(query, slug)) return slug;
  }
  for (const [slug, info] of Object.entries(INTEGRATION_DISPLAY)) {
    if (mentions(query, info.name)) return slug;
  }
  // Fallback: first word if it looks like an integration name
  const words = query
    .replace(/find\s+tools?\s+(for\s+)?/i, "")
    .trim()
    .split(/\s+/);
  if (words[0] && words[0].length > 2) return words[0];
  return null;
}

/**
 * Extract auth toolkit from meta-auth tool input or result.
 */
export function extractAuthToolkit(
  toolInput: unknown,
  result: unknown
): string | null {
  // From input — singular fields. Model-authored args: NEVER trust a cast —
  // every read is typeof-guarded (an object here white-screened the transcript,
  // staging task 04eef79f, 2026-08-22).
  if (toolInput && typeof toolInput === "object") {
    const input = toolInput as Record<string, unknown>;
    const app = [input.app, input.toolkit, input.appName].find(
      (v): v is string => typeof v === "string" && v !== ""
    );
    if (app) return app.toLowerCase();

    // Plural array fields. Two wire shapes exist: the classic string list
    // (toolkits: ["slack"]) and Composio 0.17's multi-account object list
    // (toolkits: [{ name: "posthog", action: "add" }]).
    const toolkits = input.toolkits || input.apps || input.app_names;
    if (Array.isArray(toolkits) && toolkits.length > 0) {
      const first: unknown = toolkits[0];
      if (typeof first === "string" && first !== "") return first.toLowerCase();
      if (first && typeof first === "object") {
        const name = (first as Record<string, unknown>).name;
        if (typeof name === "string" && name !== "") return name.toLowerCase();
      }
    }
  }

  // From result string — look for toolkit field
  if (result) {
    const str = typeof result === "string" ? result : JSON.stringify(result);
    const match = str.match(/\\?"toolkit\\?"\s*:\s*\\?"([a-zA-Z0-9_-]+)\\?"/i);
    if (match) return match[1].toLowerCase();
    // From connect URL path
    const urlMatch = str.match(
      /connect\.composio\.dev\/[^\s"']*\/([a-zA-Z0-9_-]+)/i
    );
    if (urlMatch) return urlMatch[1].toLowerCase();
  }

  return null;
}
